import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import type { RowPinningState } from '@tanstack/react-table';
import type {
  MRT_ColumnDef,
  MRT_ColumnOrderState,
  MRT_SortingState,
  MRT_TablePaperProps,
  MRT_TableState,
  MRT_Updater,
  MRT_VisibilityState,
} from 'material-react-table';
import { MaterialReactTable } from 'material-react-table';
import type { ReactNode } from 'react';
import { useCallback, useMemo, useRef } from 'react';
import { MdExpandLess, MdExpandMore } from 'react-icons/md';
import {
  baseTableDisplayColumnDefOptions,
  baseTableHeadProps,
  baseTableProps,
  getBaseTableBodyCellProps,
  getBaseTableBodyRowProps,
  getBaseTableContainerProps,
  getBaseTableDetailPanelProps,
  getBaseTableHeadCellProps,
  getBaseTablePaperProps,
  type BaseTablePinHoldState,
  type BaseTableRowData,
  type BaseTableStyleOverrides,
} from './baseTableMrtProps';
import BaseTableOptions from './BaseTableOptions';
import {
  useBaseTablePreferences,
  type BaseTableColumnDef,
  type BaseTableState,
} from './baseTablePreferences';

type BaseTableProps<TData extends BaseTableRowData> = {
  rows?: TData[];
  columns?: BaseTableColumnDef<TData>[];
  dropdownMenu?: (row: TData) => ReactNode;
  onRowClick?: (row: TData) => void;
  selectedRowId?: unknown;
  selectedRowIds?: unknown[];
  expandable?: boolean;
  altRowSettings?: { alternate?: boolean; color1?: string; color2?: string };
  customStyles?: BaseTableStyleOverrides;
  muiTableContainerProps?: object;
  muiTablePaperProps?: Partial<MRT_TablePaperProps<TData>>;
  enableRowVirtualization?: boolean;
  enableColumnOrdering?: boolean;
  layoutMode?: 'grid' | 'grid-no-grow' | 'semantic';
  enableTableOptions?: boolean;
  tableOptionsClassName?: string;
  tableOptionsStart?: ReactNode;
  tablePreferenceKey?: string;
  initialState?: Partial<MRT_TableState<TData>>;
  state?: Partial<MRT_TableState<TData>>;
  onColumnOrderChange?: (updater: MRT_Updater<MRT_ColumnOrderState>) => void;
  onRowPinningChange?: (updater: MRT_Updater<RowPinningState>) => void;
  onColumnVisibilityChange?: (updater: MRT_Updater<MRT_VisibilityState>) => void;
  onSortingChange?: (updater: MRT_Updater<MRT_SortingState>) => void;
  enableRowPinning?: boolean;
  rowPinningHoldDelay?: number;
  customEmptyMessage?: string;
  preserveSelectedRowOnHover?: boolean;
};

const BaseTable = <TData extends BaseTableRowData>({
  rows = [],
  columns = [],
  dropdownMenu = () => <div>No Menu</div>,
  onRowClick,
  selectedRowId = null,
  selectedRowIds = [],
  expandable = false,
  altRowSettings = {
    alternate: true,
    color1: 'var(--color-primary-shadow)',
    color2: 'var(--color-base)',
  },
  customStyles = {},
  muiTableContainerProps = {},
  muiTablePaperProps = {},
  enableRowVirtualization = true,
  enableColumnOrdering = true,
  layoutMode,
  enableTableOptions = true,
  tableOptionsClassName = '',
  tableOptionsStart,
  tablePreferenceKey,
  initialState = {},
  state,
  onColumnOrderChange,
  onRowPinningChange,
  onColumnVisibilityChange,
  onSortingChange,
  enableRowPinning = false,
  rowPinningHoldDelay = 650,
  customEmptyMessage = 'No records to display',
  preserveSelectedRowOnHover = false,
}: BaseTableProps<TData>): JSX.Element => {
  const rowPinHoldRef = useRef<BaseTablePinHoldState>({ didPin: false, timer: null });
  // Only the four persisted keys belong to the preference store; the rest of
  // initialState is MRT's own table state and is passed straight to MRT below.
  const initialTablePreferenceState: BaseTableState = {
    columnOrder: initialState.columnOrder,
    columnVisibility: initialState.columnVisibility,
    sorting: initialState.sorting,
    rowPinning: initialState.rowPinning,
  };
  const {
    columnOptions,
    saveTablePreferences,
    setColumnOrder,
    setRowPinning,
    setColumnVisibility,
    setSorting,
    storageKey,
    tablePreferences,
  } = useBaseTablePreferences({
    columns,
    initialState: initialTablePreferenceState,
    onColumnOrderChange,
    onRowPinningChange,
    onColumnVisibilityChange,
    onSortingChange,
    state,
    tablePreferenceKey,
  });

  const getBaseTableRowId = useCallback((row: TData, index: number): string => {
    const rowId = row?.id ?? row?._id ?? row?.key;
    return rowId == null ? `${index}` : `${rowId}`;
  }, []);

  const togglePinnedRow = useCallback(
    (rowId: string): void => {
      setRowPinning((currentPinning = {}) => {
        const currentTop = Array.isArray(currentPinning.top) ? currentPinning.top : [];
        const isPinned = currentTop.includes(rowId);
        return {
          ...currentPinning,
          top: isPinned
            ? currentTop.filter((pinnedId) => pinnedId !== rowId)
            : [rowId, ...currentTop],
          bottom: currentPinning.bottom || [],
        };
      });
    },
    [setRowPinning],
  );

  const effectiveColumnOrder = useMemo(
    () => tablePreferences.columnOrder.filter((columnId) => columnId !== 'mrt-row-pin'),
    [tablePreferences.columnOrder],
  );

  const tableRows = useMemo(() => {
    if (!enableRowPinning) return rows;

    const pinnedRowIds = new Set(tablePreferences.rowPinning.top || []);

    return rows.map((row, index) => {
      const rowId = getBaseTableRowId(row, index);

      return {
        ...row,
        __baseTableRowPinning: {
          isPinned: pinnedRowIds.has(rowId),
          toggle: () => togglePinnedRow(rowId),
        },
      };
    });
  }, [enableRowPinning, getBaseTableRowId, rows, tablePreferences.rowPinning.top, togglePinnedRow]);

  return (
    <div className="fusion-base-table d-flex flex-column flex-grow-1 min-h-0">
      {enableTableOptions && (
        <BaseTableOptions
          className={tableOptionsClassName}
          columnOptions={columnOptions}
          initialState={initialState}
          saveTablePreferences={saveTablePreferences}
          setColumnOrder={setColumnOrder}
          setRowPinning={setRowPinning}
          setColumnVisibility={setColumnVisibility}
          setSorting={setSorting}
          storageKey={storageKey}
          startContent={tableOptionsStart}
          tablePreferences={tablePreferences}
        />
      )}

      <MaterialReactTable<TData>
        enableColumnFilters={false}
        initialState={{ showColumnFilters: false, ...initialState }}
        columnFilterDisplayMode="popover"
        enableColumnActions={false}
        enableHiding
        enableColumnDragging={false}
        enableColumnOrdering={enableColumnOrdering}
        layoutMode={layoutMode}
        state={{
          ...state,
          columnOrder: effectiveColumnOrder,
          columnVisibility: tablePreferences.columnVisibility,
          rowPinning: tablePreferences.rowPinning,
          sorting: tablePreferences.sorting,
        }}
        onColumnOrderChange={setColumnOrder}
        onColumnVisibilityChange={setColumnVisibility}
        onRowPinningChange={setRowPinning}
        onSortingChange={setSorting}
        icons={{
          ArrowDownwardIcon: KeyboardArrowDownIcon,
          SortIcon: KeyboardArrowDownIcon,
          SyncAltIcon: () => null,
        }}
        // Safe: BaseTableColumnDef only widens columnDefType. See its doc comment.
        columns={columns as MRT_ColumnDef<TData, any>[]}
        data={tableRows}
        getRowId={enableRowPinning ? getBaseTableRowId : undefined}
        enableBottomToolbar={false}
        enableTopToolbar={false}
        enableExpanding={expandable}
        enableExpandAll={false}
        enableRowPinning={enableRowPinning}
        rowPinningDisplayMode="select-top"
        enableRowVirtualization={enableRowVirtualization}
        enablePagination={false}
        rowVirtualizerOptions={{ estimateSize: () => 70 }}
        muiExpandButtonProps={({ row }) => ({
          children: row.getIsExpanded() ? <MdExpandLess /> : <MdExpandMore />,
          sx: { display: 'none' },
        })}
        muiTablePaperProps={getBaseTablePaperProps(muiTablePaperProps, customStyles)}
        muiTableProps={baseTableProps}
        muiTableContainerProps={getBaseTableContainerProps(muiTableContainerProps)}
        displayColumnDefOptions={baseTableDisplayColumnDefOptions}
        muiTableHeadProps={baseTableHeadProps}
        muiTableHeadCellProps={getBaseTableHeadCellProps(customStyles, enableColumnOrdering)}
        muiTableBodyCellProps={getBaseTableBodyCellProps(customStyles)}
        muiTableBodyRowProps={getBaseTableBodyRowProps({
          altRowSettings,
          customStyles,
          expandable,
          onRowClick,
          preserveSelectedRowOnHover,
          rowPinHoldRef: enableRowPinning ? rowPinHoldRef : undefined,
          rowPinningHoldDelay,
          selectedRowId,
          selectedRowIds,
          togglePinnedRow: enableRowPinning ? togglePinnedRow : undefined,
        })}
        muiDetailPanelProps={getBaseTableDetailPanelProps(customStyles)}
        renderDetailPanel={({ row }) => (
          <div className="w-100" onClick={(event) => event.stopPropagation()}>
            {dropdownMenu(row.original)}
          </div>
        )}
        renderEmptyRowsFallback={() => (
          <div className="text-center p-4 fw-bold" style={{ color: 'var(--color-font)' }}>
            {customEmptyMessage || 'No records to display'}
          </div>
        )}
      />
    </div>
  );
};

export default BaseTable;
